import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { palette } from "../../styles/palette";
import { typography } from "../../styles/typography";
import { fetchContents, ContentItem } from "@/api/contentApi";
import { fontFamily } from "@/styles/font";
import logger from "@/utils/logger";

const ContentPage: React.FC = () => {
    // 상태 관리
    const [contents, setContents] = useState<ContentItem[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadContents = async () => {
            try {
                setLoading(true);
                const contentData = await fetchContents();
                setContents(contentData);
            } catch (err) {
                logger.error("콘텐츠 로드 실패:", err);
                setError("콘텐츠를 불러오는데 실패했습니다.");
            } finally {
                setLoading(false);
            }
        };

        loadContents();
    }, []);

    return (
        <Container>
            <HeaderSection>
                <Title>콘텐츠</Title>
                <CategoryContainer>{/* 카테고리 필터 컴포넌트가 여기에 들어갈 예정 */}</CategoryContainer>
            </HeaderSection>

            <ContentSection>
                <ContentListWrapper>
                    {loading ? (
                        <LoadingMessage>콘텐츠를 불러오는 중입니다...</LoadingMessage>
                    ) : error ? (
                        <ErrorMessage>{error}</ErrorMessage>
                    ) : !contents || contents.length === 0 ? (
                        <EmptyContent>
                            <EmptyIcon>✨</EmptyIcon>
                            <EmptyText>콘텐츠를 준비하고 있어요!</EmptyText>
                            <EmptySubText>그동안 우리술을 즐길 수 있는 공간을 소개할게요.</EmptySubText>
                            <LinkButton href="https://maily.so/soolhorang" target="_blank" rel="noopener noreferrer">
                                눌러서 확인해보세요!
                            </LinkButton>
                        </EmptyContent>
                    ) : (
                        <>
                            <ContentList>
                                {contents.map((content, index) => (
                                    <ContentCard
                                        key={index}
                                        href={content.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <CardContent>
                                            <ContentTitle>{content.title}</ContentTitle>
                                            <ContentDescription>{content.description}</ContentDescription>
                                            <ContentDate>{content.date}</ContentDate>
                                        </CardContent>
                                    </ContentCard>
                                ))}
                            </ContentList>
                            <ViewMoreButton
                                href="https://maily.so/soolhorang"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                더 보기
                            </ViewMoreButton>
                        </>
                    )}
                </ContentListWrapper>
            </ContentSection>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
    width: 100%;
    max-width: 430px;
    min-width: 320px;
    height: 100vh;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    background: ${palette.white};
    overflow-y: auto;
`;

const HeaderSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid ${palette.gray[200]};
    background-color: ${palette.white};
    padding-top: 12px;
`;

const Title = styled.h1`
    ${typography.title2}
    color: ${palette.gray[900]};
    text-align: center;
    margin-bottom: 16px;
`;

const CategoryContainer = styled.div`
    padding: 0 16px 12px;
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    padding-bottom: 80px; /* 바텀 네비게이션 높이보다 크게 패딩 추가 */
`;

const ContentListWrapper = styled.div`
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
`;

const LoadingMessage = styled.div`
    ${typography.body1}
    color: ${palette.gray[700]};
    margin: 40px 0;
    text-align: center;
`;

const ErrorMessage = styled.div`
    ${typography.body1}
    color: ${palette.red};
    margin: 40px 0;
    text-align: center;
`;

const EmptyContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 40px 0;
    padding: 20px;
`;

const EmptyIcon = styled.div`
    font-size: 40px;
    margin-bottom: 16px;
`;

const EmptyText = styled.p`
    ${typography.title3}
    color: ${palette.gray[700]};
    margin-bottom: 8px;
    text-align: center;
`;

const EmptySubText = styled.p`
    ${typography.body2}
    color: ${palette.gray[600]};
    text-align: center;
    max-width: 280px;
`;

const LinkButton = styled.a`
    ${typography.button}
    color: ${palette.gray[900]};
    margin-top: 24px;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: ${palette.primary};
    }
`;

const ContentList = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 16px;
`;

const ContentCard = styled.a`
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 12px;
    padding: 16px;
    text-decoration: none;
    color: inherit;
    transition:
        transform 0.2s,
        box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
`;

const CardContent = styled.div``;

const ContentTitle = styled.h3`
    font-family: ${fontFamily.base};
    font-weight: 700;
    font-size: 16px;
    line-height: 1.3;
    color: #1f1f1f;
    margin: 0 0 8px 0;
`;

const ContentDescription = styled.p`
    font-family: ${fontFamily.base};
    font-weight: 400;
    font-size: 14px;
    line-height: 1.4;
    color: #767676;
    margin: 0 0 12px 0;
`;

const ContentDate = styled.span`
    font-family: ${fontFamily.base};
    font-weight: 350;
    font-size: 12px;
    color: #999999;
`;

const ViewMoreButton = styled.a`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 40px;
    background: #ffffff;
    border: 1px solid #007f73;
    border-radius: 8px;
    font-family: ${fontFamily.base};
    font-weight: 500;
    font-size: 14px;
    color: #007f73;
    margin: 24px auto 0;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f2f9f8;
    }
`;

export default ContentPage;
